import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Lock } from "lucide-react";
import { EnquiryCard } from "../components/site/EnquiryCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "pt_owner_token";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

const Login = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      onLogin(data.token);
    } catch (e2) {
      setErr(errText(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form data-testid="admin-login-form" onSubmit={submit} className="mx-auto mt-10 max-w-sm border border-ink/10 bg-white/60 p-8">
      <Lock className="text-gold" size={22} />
      <h1 className="mt-4 font-display text-3xl">Enquiries inbox</h1>
      <p className="mt-2 text-sm text-clay">Owner access only.</p>
      <input data-testid="admin-password-input" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className="mt-8 w-full border-b border-ink/20 bg-transparent py-3 text-sm outline-none focus:border-gold" />
      {err && <p data-testid="admin-login-error" className="mt-3 text-sm text-crimson">{err}</p>}
      <button data-testid="admin-login-btn" disabled={busy} className="mt-8 w-full rounded-full bg-crimson py-3.5 text-sm font-semibold text-parchment transition-colors duration-300 hover:bg-crimson-deep disabled:opacity-60">
        {busy ? "Checking…" : "Open inbox"}
      </button>
    </form>
  );
};

const AdminBar = () => (
  <header className="flex items-center justify-between px-6 py-5 sm:px-12 lg:px-20">
    <Link to="/" data-testid="admin-logo-link"><img src="/logo-full.png" alt="Praveen Textiles" className="h-12 w-auto sm:h-14" /></Link>
  </header>
);

const FILTERS = [["all", "All"], ["new", "New"], ["handled", "Handled"]];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(KEY));
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const logout = useCallback(() => {
    localStorage.removeItem(KEY);
    setToken(null);
    setItems(null);
  }, []);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/enquiries`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data))
      .catch((e) => { if (e?.response?.status === 401) logout(); else toast.error(errText(e)); });
  }, [token, logout]);

  const login = (t) => { localStorage.setItem(KEY, t); setToken(t); };

  const toggle = async (en) => {
    try {
      const { data } = await axios.patch(`${API}/admin/enquiries/${en.id}`, { handled: !en.handled }, auth);
      setItems((list) => list.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      if (e?.response?.status === 401) logout(); else toast.error(errText(e));
    }
  };

  if (!token) return <><AdminBar /><main className="px-5 pb-24 pt-6"><Login onLogin={login} /></main></>;

  const newCount = items?.filter((x) => !x.handled).length ?? 0;
  const shown = (items || []).filter((x) => filter === "all" || (filter === "new" ? !x.handled : x.handled));

  return (
    <><AdminBar />
    <main data-testid="admin-inbox" className="px-5 pb-24 pt-6 sm:px-10">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Owner · Sales desk</p>
            <h1 className="mt-3 font-display text-4xl font-medium sm:text-5xl">Enquiries</h1>
            <p data-testid="admin-new-count" className="mt-2 text-sm text-clay">{items ? `${items.length} total · ${newCount} new` : "Loading…"}</p>
          </div>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-xs font-semibold hover:border-crimson hover:text-crimson">
            <LogOut size={14} /> Log out
          </button>
        </div>
        <div className="mt-8 flex gap-2">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`admin-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-300 ${filter === k ? "bg-crimson text-parchment" : "border border-ink/15 text-ink/70 hover:border-ink/40"}`}>{l}</button>
          ))}
        </div>
        <div data-testid="admin-inbox-list" className="mt-8 space-y-4">
          {items && shown.length === 0 && <p data-testid="admin-empty" className="border border-dashed border-ink/20 p-10 text-center text-sm text-clay">No enquiries here yet.</p>}
          {shown.map((en) => <EnquiryCard key={en.id} en={en} onToggle={() => toggle(en)} />)}
        </div>
      </div>
    </main>
    </>
  );
}
